import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import UserDrawer from './components/UserDrawer';
import { registrationService, type Registration } from '../../../services/registration.service';

const typeConfig: Record<string, { label: string; color: string }> = {
  webinar: { label: 'וובינר', color: 'bg-violet-50 text-violet-600 border-violet-200' },
  workshop: { label: 'סדנה', color: 'bg-amber-50 text-amber-600 border-amber-200' },
  training: { label: 'הכשרה', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
};

function formatWhatsApp(phone: string | null): string {
  if (!phone) return '972';
  const digits = phone.replace(/[\s\-]/g, '');
  if (digits.startsWith('0')) return '972' + digits.slice(1);
  return digits;
}

function exportToCSV(users: Registration[]) {
  const headers = ['שם', 'טלפון', 'אימייל', 'עיר', 'פעילות', 'תאריך הרשמה', 'מוכר ברווחה', 'טווח שכר', 'אינו עובד מעל שנה'];
  const rows = users.map((u) => [
    u.name || '', u.phone || '', u.email || '', u.city || '', u.registerable?.title || u.registerable?.company || '', u.registration_date,
    u.known_in_welfare ? 'כן' : 'לא', u.salary_range || '', u.unemployed_over_year ? 'כן' : 'לא',
  ]);

  const BOM = '\uFEFF';
  const csvContent = BOM + [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `נרשמים_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function UsersPage() {
  const [users, setUsers] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<Registration | null>(null);
  const [exportDone, setExportDone] = useState(false);

  useEffect(() => {
    registrationService.list().then((data) => { setUsers(data); setLoading(false); });
  }, []);

  const filtered = users.filter((u) =>
    (u.name || '').includes(search) || (u.phone || '').includes(search) || (u.city || '').includes(search)
  );

  const handleExport = () => {
    exportToCSV(filtered);
    setExportDone(true);
    setTimeout(() => setExportDone(false), 2500);
  };

  return (
    <AdminLayout title="נרשמים" subtitle="רשימת כל הנרשמים לפעילויות">
      {exportDone && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-emerald-600 text-sm font-semibold">
          <i className="ri-checkbox-circle-line text-base"></i>
          הקובץ יוצא בהצלחה!
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="relative flex items-center">
          <div className="w-4 h-4 flex items-center justify-center absolute right-3 text-gray-400"><i className="ri-search-line text-sm"></i></div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש לפי שם, טלפון..."
            className="pr-9 pl-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-white focus:outline-none focus:border-rose-300 w-64 transition-all" dir="rtl" />
        </div>
        <button onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-600 text-sm font-bold hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-base"></i></div>
          ייצוא CSV ({filtered.length})
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-3">{filtered.length} נרשמים נמצאו</p>

      {loading ? (
        <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-rose-100 overflow-hidden overflow-x-auto">
          <div className="grid grid-cols-[2fr_2fr_2.5fr_1fr_1fr_auto] gap-0 border-b-2 border-rose-100 bg-rose-50/60 px-4 py-3 min-w-[700px]">
            {['שם', 'טלפון + וואטסאפ', 'פעילות', 'סוג', 'תאריך הרשמה', ''].map((h) => (
              <span key={h} className="text-xs font-bold text-gray-500 px-2">{h}</span>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="divide-y-2 divide-rose-50">
              {filtered.map((user) => {
                const waNumber = formatWhatsApp(user.phone);
                const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent('שלום, ראיתי שנרשמת לפעילות שלנו 😊')}`;

                return (
                  <div key={user.id}
                    className="grid grid-cols-[2fr_2fr_2.5fr_1fr_1fr_auto] gap-0 px-4 py-3 items-center hover:bg-rose-50/30 transition-colors group cursor-pointer min-w-[700px]"
                    onClick={() => setSelectedUser(user)}>
                    <div className="flex items-center gap-2.5 px-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 font-bold text-xs flex-shrink-0">{(user.name || '?').charAt(0)}</div>
                      <div className="min-w-0"><p className="text-xs font-semibold text-gray-800 truncate">{user.name}</p>{user.city && <p className="text-xs text-gray-400 truncate">{user.city}</p>}</div>
                    </div>
                    <div className="px-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <a href={waLink} target="_blank" rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-500 hover:bg-emerald-100 transition-all cursor-pointer flex-shrink-0"
                        title="פתח וואטסאפ"><i className="ri-whatsapp-line text-sm"></i></a>
                      <p className="text-xs text-gray-600 font-medium leading-none" dir="ltr">{user.phone}</p>
                    </div>
                    <div className="px-2"><p className="text-xs text-gray-600 truncate leading-snug">{user.registerable?.title || user.registerable?.company || ''}</p></div>
                    <div className="px-2"><span className="text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap bg-violet-50 text-violet-600 border-violet-200">{user.registerable_type?.includes('Job') ? 'משרה' : 'פעילות'}</span></div>
                    <div className="px-2"><p className="text-xs text-gray-500">{user.registration_date}</p></div>
                    <div className="px-2">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedUser(user); }}
                        className="px-3 py-1.5 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-500 text-xs font-semibold hover:bg-rose-100 transition-all cursor-pointer whitespace-nowrap opacity-0 group-hover:opacity-100">צפייה</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-rose-50 border-2 border-rose-100 mb-3"><i className="ri-group-line text-2xl text-rose-300"></i></div>
              <p className="text-gray-500 font-semibold text-sm">לא נמצאו נרשמים</p>
              <p className="text-xs text-gray-400 mt-1">נסה לשנות את הפילטרים</p>
            </div>
          )}
        </div>
      )}

      {selectedUser && <UserDrawer user={selectedUser} onClose={() => setSelectedUser(null)} />}
    </AdminLayout>
  );
}
